import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"

// =====================
// Type definitions des réponses des services
// =====================
/**
 * Réponse de l'API pour la connexion (POST /login)
 */
export type LoginResponse = {
    token: string
    user?: {
        _id: string
        username: string
        lastname: string
        firstname: string
    }
    adminHomeys?: boolean
    multipleCompany?: boolean
}

/**
 * Réponse de l'API pour la validation de compte (PUT /v5/user/:_id_user/validation)
 */
export type UpdateUserAccountValidationResponse = {
    token: string
    company?: { idcompany?: string }
    [key: string]: unknown
}

/**
 * Données entreprise pour PUT /v5/user/:_id_user/changecompany
 */
export type UpdateUserCompanyData = {
    address?: unknown
    idcompany?: string
    legalForm?: string
}

/**
 * Réponse de l'API pour la mise à jour de l'entreprise (PUT /v5/user/:_id_user/changecompany)
 */
export type UpdateUserCompanyResponse = Record<string, unknown>

/**
 * Réponse de l'API pour l'envoi du mail de validation (POST /v5/user/sendvalidation)
 */
export type SendUserAccountValidationMailResponse = Record<string, unknown>

/**
 * Réponse de l'API pour le changement de mot de passe (PUT /auth/user/change-password)
 */
export type UpdateUserPasswordResponse = Record<string, unknown>

// =====================
// Service
// =====================

/**
 * Le service permettant de gérer la connexion et l'authentification
 */
export class AuthService {
    /**
     * Connexion d'un utilisateur
     * POST /api/metier/login
     * En cas d'erreur invalid_user_not_validated, l'appel lance une ServiceError avec code "invalid_user_not_validated".
     * @param username - Email de l'utilisateur
     * @param password - Mot de passe
     * @returns Réponse avec token et éventuellement user, multipleCompany
     */
    async login(username: string, password: string): Promise<LoginResponse> {
        return handleServiceCall<LoginResponse>(async () => {
            const { data } = await axios.post<LoginResponse>(
                "/api/metier/login",
                { username, password },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-no-authorization": true,
                    },
                }
            )
            return {
                token: data?.token ?? "",
                user: data?.user,
                adminHomeys: data?.adminHomeys,
                multipleCompany: data?.multipleCompany,
            }
        })
    }

    /**
     * Met à jour le mot de passe avec un code de validation
     * PUT /api/metier/auth/user/change-password
     * @param params - userId, password, validationCode
     * @returns Utilisateur mis à jour
     */
    async updateUserPassword(params: {
        userId: string
        password: string
        validationCode: string
    }): Promise<UpdateUserPasswordResponse> {
        return handleServiceCall<UpdateUserPasswordResponse>(async () => {
            const { data } = await axios.put<UpdateUserPasswordResponse>(
                `/api/metier/auth/user/change-password`,
                {
                    password: params.password,
                    _id_user: params.userId,
                    validationCode: params.validationCode,
                },
                {
                    headers: {
                        "x-no-authorization": true,
                    },
                }
            )
            return data ?? {}
        })
    }

    /**
     * Envoie le mail de validation de compte
     * POST /api/metier/v5/user/sendvalidation
     * @param params - userId (optionnel), userName (optionnel) — au moins l'un des deux requis
     * @returns Réponse API
     */
    async sendUserAccountValidationMail(params: {
        userId?: string
        userName?: string
    }): Promise<SendUserAccountValidationMailResponse> {
        return handleServiceCall<SendUserAccountValidationMailResponse>(
            async () => {
                const body: { _id_user?: string; username?: string } = {}
                if (params.userId) body._id_user = params.userId
                if (params.userName) body.username = params.userName
                const { data } =
                    await axios.post<SendUserAccountValidationMailResponse>(
                        "/api/metier/v5/user/sendvalidation",
                        body,
                        {
                            headers: {
                                "x-no-authorization": true,
                            },
                        }
                    )
                return data ?? {}
            }
        )
    }

    /**
     * Valide le compte utilisateur avec le code reçu par mail
     * PUT /api/metier/v5/user/:userId/validation
     * @param params - userId, validationCode
     * @returns Objet user étendu avec token
     */
    async updateUserAccountValidation(params: {
        userId: string
        validationCode: string
    }): Promise<UpdateUserAccountValidationResponse> {
        return handleServiceCall<UpdateUserAccountValidationResponse>(
            async () => {
                const { data } =
                    await axios.put<UpdateUserAccountValidationResponse>(
                        `/api/metier/v5/user/${params.userId}/validation`,
                        { validationCode: params.validationCode },
                        {
                            headers: {
                                "x-no-authorization": true,
                            },
                        }
                    )
                return data ?? { token: "" }
            }
        )
    }

    /**
     * Met à jour l'entreprise de l'utilisateur avec un code de validation
     * PUT /api/metier/v5/user/:userId/changecompany
     * @param params - userId, validationCode, dataCompany
     * @returns Entreprise mise à jour
     */
    async updateUserCompany(params: {
        userId: string
        validationCode: string
        dataCompany: UpdateUserCompanyData
    }): Promise<UpdateUserCompanyResponse> {
        return handleServiceCall<UpdateUserCompanyResponse>(async () => {
            const { data } = await axios.put<UpdateUserCompanyResponse>(
                `/api/metier/v5/user/${params.userId}/changecompany`,
                {
                    validationCode: params.validationCode,
                    dataCompany: params.dataCompany,
                },
                {
                    headers: {
                        "x-no-authorization": true,
                    },
                }
            )
            return data ?? {}
        })
    }
}
